/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import React from 'react';
import { Link } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Label } from 'reactstrap';
import { Field } from 'redux-form';
import {
  emptyOrMaxLength100,
  required,
} from '@hbrisk/sp3-risk-model-support/utility/form/validation/fieldValidators/index.js';
import SubmitButton from '#components/support/buttons/SubmitButton/index.jsx';
import EmailFieldWithLabelAndFeedback from '#components/support/forms/EmailField/index.jsx';
import { FieldFeedback, RenderInput } from '#components/support/forms/utility/index.js';
import OrgSelectMenu from '../../support/OrgSelectMenu/index.js';

type Props = {|
  error?: string | null,
  form: string,
  handleSubmit: Function,
  submitSucceeded: boolean,
  submitting: boolean,
  hasTouchedFields: boolean,
|};

const CreateUserForm = ({
  error = null,
  form,
  handleSubmit,
  submitting,
  submitSucceeded,
  hasTouchedFields,
}: Props): Element<typeof Form> => {
  const showSuccess = submitSucceeded && !hasTouchedFields;
  const errorAlert = error ? <div className="alert alert-danger">{error}</div> : null;
  const successAlert = showSuccess ? (
    <div className="alert alert-success">User created successfully! Add another user below.</div>
  ) : null;

  return (
    <Form className="create-user-form" onSubmit={handleSubmit}>
      {successAlert}
      {errorAlert}
      <FormGroup tag="fieldset">
        <legend>General</legend>
        <FormGroup row>
          <Col lg="6">
            <Label htmlFor="firstName">First Name</Label>
            <Field
              id="firstName"
              name="firstName"
              type="text"
              component={RenderInput}
              validate={[required, emptyOrMaxLength100]}
            />
            <FieldFeedback name="firstName" form={form} />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col lg="6">
            <Label htmlFor="lastName">Last Name</Label>
            <Field
              id="lastName"
              name="lastName"
              type="text"
              component={RenderInput}
              validate={[required, emptyOrMaxLength100]}
            />
            <FieldFeedback name="lastName" form={form} />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col lg="6">
            <EmailFieldWithLabelAndFeedback form={form} />
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col lg="6">
            <Label htmlFor="orgId">Organization</Label>
            <OrgSelectMenu name="orgId" validate={[required]} />
            <FieldFeedback name="orgId" form={form} />
          </Col>
        </FormGroup>
      </FormGroup>
      <FormGroup tag="fieldset">
        <legend>Permissions</legend>
        <FormGroup row check>
          <Col lg="5" className="ml-3">
            <Label check>
              <Field id="isAdmin" name="isAdmin" type="checkbox" component={RenderInput} />
              Is Admin
            </Label>
          </Col>
        </FormGroup>
      </FormGroup>
      <div className="d-flex align-items-center justify-content-between">
        <div>
          <Button color="link" tag={Link} to="..">
            Cancel
          </Button>
        </div>
        <div className="d-flex align-items-center">
          <Label check className="mr-3 mb-0">
            <Field
              id="addAdditional"
              name="addAdditional"
              type="checkbox"
              component={RenderInput}
              className="mr-1"
            />
            <small>Add multiple users</small>
          </Label>
          <SubmitButton submitting={submitting}>Create</SubmitButton>
        </div>
      </div>
    </Form>
  );
};

export default CreateUserForm;
